/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Brand Domain & Logo settings tab.
 *
 * Organisation-level configuration of brand domains (bring-your-own-domain): one table
 * (root domain · brand host · logo · default · actions) and a right-side drawer used for
 * both add and edit. DNS / certificate status is intentionally not shown here (CF-02);
 * domain binding is handled by the customer's IT via the platform onboarding process.
 */

import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {IxModule, ModalService, ToastService} from '@siemens/ix-angular';
import {firstValueFrom} from 'rxjs';

import {
  HOSTNAME_MAX_LENGTH,
  HOSTNAME_REGEX,
  isApexDomain,
  normalizeHost,
  suggestBrandHost,
} from '@product-passport/organization/util';

import {
  BRAND_LOGO_ACCEPTED_TYPES,
  BRAND_LOGO_MAX_BYTES,
  BRAND_LOGO_MIN_HEIGHT_PX,
  BrandDomain,
  BrandDomainLogoMode,
  CreateBrandDomain,
  UpdateBrandDomain,
} from '../../../models/BrandDomain';
import {BrandDomainService} from '../../../services/brand-domain.service';
import {UserService} from '../../../services/user.service';

type DomainForm = FormGroup<{
  host: FormControl<string>;
  label: FormControl<string>;
  isDefault: FormControl<boolean>;
  logoMode: FormControl<BrandDomainLogoMode>;
  logoUrl: FormControl<string>;
  refDomainId: FormControl<string>;
}>;

/** Table row: the brand domain plus its resolved logo, computed once per load. */
interface BrandDomainRow {
  domain: BrandDomain;
  logoPreview: string | null;
}

/** Accepts hosts or URLs in any case; the backend receives the normalised host. */
function hostnameValidator(
  control: AbstractControl<string>,
): ValidationErrors | null {
  if (!control.value) {
    return null;
  }
  const host = normalizeHost(control.value);
  return host && HOSTNAME_REGEX.test(host) ? null : {hostname: true};
}

@Component({
  selector: 'app-brand-domain-settings',
  templateUrl: './brand-domain-settings.component.html',
  styleUrl: './brand-domain-settings.component.scss',
  standalone: true,
  imports: [IxModule, ReactiveFormsModule, TranslateModule],
})
export class BrandDomainSettingsComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly brandDomainService = inject(BrandDomainService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('deleteModal', {read: TemplateRef})
  private readonly deleteModalRef!: TemplateRef<unknown>;

  protected orgId = '';
  protected loading = true;
  protected saving = false;
  protected domains: BrandDomain[] = [];
  /** Rows rendered by the table; rebuilt whenever `domains` changes. */
  protected rows: BrandDomainRow[] = [];

  protected drawerOpen = false;
  protected editing: BrandDomain | null = null;
  protected errorMessages: string[] = [];
  protected logoError: string | null = null;
  protected logoFileName: string | null = null;

  protected readonly form: DomainForm = this.fb.group({
    host: this.fb.control('', [
      Validators.required,
      Validators.maxLength(HOSTNAME_MAX_LENGTH),
      hostnameValidator,
    ]),
    label: this.fb.control('', [Validators.maxLength(100)]),
    isDefault: this.fb.control(false),
    logoMode: this.fb.control<BrandDomainLogoMode>('NONE'),
    logoUrl: this.fb.control(''),
    refDomainId: this.fb.control(''),
  });

  ngOnInit(): void {
    this.userService.userDataChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (user?.organization?.id && user.organization.id !== this.orgId) {
          this.orgId = user.organization.id;
          void this.loadDomains();
        }
      });
  }

  /** Brand domains that carry an uploaded logo and can therefore be referenced. */
  protected get referenceableDomains(): BrandDomain[] {
    return this.domains.filter(
      (domain) =>
        domain.logo.mode === 'UPLOAD' && domain.id !== this.editing?.id,
    );
  }

  /** Derived, read-only brand host shown in the drawer (CF-04). */
  protected get derivedHost(): string {
    const host = normalizeHost(this.form.controls.host.value);
    if (!host) {
      return '';
    }
    // Registrable domains (eTLD+1, e.g. subaru.co.jp) get the fixed prefix; deeper hosts are kept
    return isApexDomain(host) ? suggestBrandHost(host) : host;
  }

  /** Resolves the logo shown for a row; `REFERENCE` entries follow the referenced upload. */
  private logoPreview(domain: BrandDomain): string | null {
    if (domain.logo.mode === 'UPLOAD') {
      return domain.logo.url ?? null;
    }
    if (domain.logo.mode === 'REFERENCE') {
      return (
        this.domains.find((d) => d.id === domain.logo.refDomainId)?.logo.url ??
        null
      );
    }
    return null;
  }

  protected openAdd(): void {
    this.editing = null;
    this.resetForm();
    this.form.controls.host.enable();
    this.form.controls.isDefault.setValue(this.domains.length === 0);
    this.drawerOpen = true;
  }

  protected openEdit(domain: BrandDomain): void {
    this.editing = domain;
    this.resetForm();
    this.form.patchValue({
      host: domain.host,
      label: domain.label ?? '',
      isDefault: domain.isDefault,
      logoMode: domain.logo.mode,
      logoUrl: domain.logo.url ?? '',
      refDomainId: domain.logo.refDomainId ?? '',
    });
    // Hosts are immutable: printed identifiers must never be re-pointed silently
    this.form.controls.host.disable();
    this.drawerOpen = true;
  }

  protected closeDrawer(): void {
    this.drawerOpen = false;
    this.editing = null;
    this.errorMessages = [];
  }

  protected onLogoModeChange(mode: BrandDomainLogoMode): void {
    this.form.controls.logoMode.setValue(mode);
    this.logoError = null;
    if (mode !== 'UPLOAD') {
      this.form.controls.logoUrl.setValue('');
      this.logoFileName = null;
    }
    if (mode !== 'REFERENCE') {
      this.form.controls.refDomainId.setValue('');
    }
  }

  protected async onLogoFilesChanged(
    event: CustomEvent<File[]>,
  ): Promise<void> {
    const file = event.detail?.[0];
    if (!file) {
      return;
    }
    this.logoError = null;
    if (!BRAND_LOGO_ACCEPTED_TYPES.includes(file.type)) {
      this.logoError = 'organizations.brand_domains.logo_error_type';
      return;
    }
    if (file.size > BRAND_LOGO_MAX_BYTES) {
      this.logoError = 'organizations.brand_domains.logo_error_size';
      return;
    }
    const dataUrl = await this.readAsDataUrl(file);
    const height = await this.readImageHeight(dataUrl);
    if (height !== null && height < BRAND_LOGO_MIN_HEIGHT_PX) {
      this.logoError = 'organizations.brand_domains.logo_error_height';
      return;
    }
    this.form.controls.logoUrl.setValue(dataUrl);
    this.logoFileName = file.name;
  }

  protected get canSave(): boolean {
    if (this.form.invalid || this.saving) {
      return false;
    }
    const {logoMode, logoUrl, refDomainId} = this.form.getRawValue();
    if (logoMode === 'UPLOAD' && !logoUrl) {
      return false;
    }
    return !(logoMode === 'REFERENCE' && !refDomainId);
  }

  protected async save(): Promise<void> {
    if (!this.canSave) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.errorMessages = [];
    const raw = this.form.getRawValue();
    const logo = this.buildLogo(raw.logoMode, raw.logoUrl, raw.refDomainId);

    try {
      if (this.editing) {
        const changes: UpdateBrandDomain = {
          label: raw.label || undefined,
          isDefault: raw.isDefault || undefined,
          logo,
        };
        await firstValueFrom(
          this.brandDomainService.update$(this.orgId, this.editing.id, changes),
        );
      } else {
        const payload: CreateBrandDomain = {
          host: this.derivedHost,
          label: raw.label || undefined,
          isDefault: raw.isDefault,
          logo,
        };
        await firstValueFrom(
          this.brandDomainService.create$(this.orgId, payload),
        );
      }
      await this.toastService.show({
        message: this.translate.instant(
          this.editing
            ? 'organizations.brand_domains.update_success'
            : 'organizations.brand_domains.create_success',
        ),
        type: 'success',
      });
      this.closeDrawer();
      await this.loadDomains();
    } catch (error) {
      this.errorMessages = this.extractErrorMessages(error);
    } finally {
      this.saving = false;
    }
  }

  protected async confirmDelete(domain: BrandDomain): Promise<void> {
    if (domain.isDefault) {
      return;
    }
    const instance = await this.modalService.open({
      content: this.deleteModalRef,
      centered: true,
      data: domain,
    });
    instance.onClose.on((confirmed) => {
      if (confirmed) {
        void this.deleteDomain(domain);
      }
    });
  }

  private async deleteDomain(domain: BrandDomain): Promise<void> {
    this.loading = true;
    try {
      await firstValueFrom(
        this.brandDomainService.delete$(this.orgId, domain.id),
      );
      await this.toastService.show({
        message: this.translate.instant(
          'organizations.brand_domains.delete_success',
        ),
        type: 'success',
      });
    } finally {
      await this.loadDomains();
    }
  }

  private async loadDomains(): Promise<void> {
    this.loading = true;
    try {
      this.domains = await firstValueFrom(
        this.brandDomainService.load$(this.orgId),
      );
      this.rows = this.domains.map((domain) => ({
        domain,
        logoPreview: this.logoPreview(domain),
      }));
    } finally {
      this.loading = false;
    }
  }

  private resetForm(): void {
    this.form.reset({
      host: '',
      label: '',
      isDefault: false,
      logoMode: 'NONE',
      logoUrl: '',
      refDomainId: '',
    });
    this.errorMessages = [];
    this.logoError = null;
    this.logoFileName = null;
  }

  private buildLogo(
    mode: BrandDomainLogoMode,
    url: string,
    refDomainId: string,
  ): CreateBrandDomain['logo'] {
    switch (mode) {
      case 'UPLOAD':
        return {mode, url};
      case 'REFERENCE':
        return {mode, refDomainId};
      default:
        return {mode: 'NONE'};
    }
  }

  private extractErrorMessages(error: unknown): string[] {
    const message = (error as {error?: {message?: string | string[]}})?.error
      ?.message;
    if (Array.isArray(message)) {
      return message;
    }
    if (typeof message === 'string') {
      return [message];
    }
    return [this.translate.instant('organizations.brand_domains.save_failed')];
  }

  private readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private readImageHeight(dataUrl: string): Promise<number | null> {
    return new Promise((resolve) => {
      if (typeof Image === 'undefined') {
        resolve(null);
        return;
      }
      const image = new Image();
      image.onload = () => resolve(image.naturalHeight || null);
      image.onerror = () => resolve(null);
      image.src = dataUrl;
    });
  }
}
